import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  countChars,
  MAX_TEXT_THOUGHT_CHARS,
  MAX_THOUGHT_SECONDS,
  MIN_TEXT_THOUGHT_CHARS,
  type ProfileResponse,
  type ThoughtSummary,
} from '@app/shared';
import { HelpHint } from '../components/HelpHint';
import { IntroCard } from '../components/IntroCard';
import { ThoughtRecorder } from '../components/ThoughtRecorder';
import { AutoTextarea, Badge, Button, Card, Skeleton, SkeletonGroup } from '../components/ui';
import { useIntroOutcome } from '../hooks/useIntro';
import { useToast } from '../hooks/useToast';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { busyInterval, POLL_MS } from '../lib/busy';
import { errorText } from '../lib/errors';
import { firstLine, THOUGHT_TONE } from '../lib/labels';
import { haptic } from '../lib/tma';

const RECENT = 5;

/** «Создать» (spec 6.1): speak or type a thought; the latest thoughts below. */
export function HomePage() {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast, show } = useToast();
  const { data } = useQuery<ProfileResponse>({ queryKey: ['profile'], enabled: false });
  const profile = data!;
  const user = profile.user;

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const chars = countChars(text);
  const left = MIN_TEXT_THOUGHT_CHARS - chars;

  const recent = useQuery<ThoughtSummary[]>({
    queryKey: ['thoughts', 'home'],
    queryFn: () => api.listThoughts(),
    refetchInterval: (q) =>
      q.state.data?.some((th) => busyInterval('thought', th.status) !== false) ? POLL_MS : false,
  });

  // How the spoken introduction ended: the card is gone by then, the page tells.
  useIntroOutcome(user.introStatus, (outcome) =>
    outcome === 'done' ? show(t.intro.done, 'success') : show(t.intro.failed, 'warning'),
  );

  const opened = (id: number) => {
    void qc.invalidateQueries({ queryKey: ['thoughts'] });
    navigate(`/thoughts/${id}`);
  };

  const sendVoice = async (blob: Blob, _mime: string, durationMs: number) => {
    try {
      opened((await api.createVoiceThought(blob, durationMs)).id);
      return true;
    } catch (err) {
      show(errorText(err, t), 'destructive');
      return false;
    }
  };

  const sendText = async () => {
    if (left > 0 || sending) return;
    setSending(true);
    try {
      opened((await api.createTextThought(text.trim())).id);
    } catch (err) {
      show(errorText(err, t), 'destructive');
    } finally {
      setSending(false);
    }
  };

  const items = (recent.data ?? []).slice(0, RECENT);

  return (
    <div className="pt-2 space-y-3">
      <Card variant="surface" padding="none" className="p-5 text-center">
        <h2 className="flex items-center justify-center gap-2 text-2xl font-extrabold mb-1">
          {t.home.heroTitle}
          <HelpHint topic="home" />
        </h2>
        <p className="text-sm text-hint mb-5">{t.home.heroText}</p>
        <ThoughtRecorder
          maxSeconds={MAX_THOUGHT_SECONDS}
          botUrl={profile.botUrl}
          onRecorded={sendVoice}
          onFailed={(err) => show(errorText(err, t), 'destructive')}
        />

        <div className="flex items-center gap-3 my-5 text-xs text-hint">
          <span className="flex-1 h-px bg-line" />
          {t.home.orType}
          <span className="flex-1 h-px bg-line" />
        </div>

        <AutoTextarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t.home.typePlaceholder}
          maxLength={MAX_TEXT_THOUGHT_CHARS}
          minRows={3}
          maxRows={12}
          className="text-left"
        />
        <div className="flex items-center justify-between gap-3 mt-3">
          <span className="text-xs text-hint text-left">{chars > 0 && left > 0 ? t.home.typeHint(left) : ''}</span>
          <Button variant="ink" size="md" disabled={left > 0} loading={sending} onClick={sendText}>
            {t.home.send}
          </Button>
        </div>
      </Card>

      <IntroCard onError={(msg) => show(msg, 'destructive')} />

      <section className="pt-2">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-bold uppercase tracking-[0.06em] text-hint">{t.home.recent}</h2>
          {items.length > 0 && (
            <Link to="/library" className="text-sm font-semibold text-fg">
              {t.home.all}
            </Link>
          )}
        </div>
        {recent.isLoading && (
          <SkeletonGroup label={t.common.loading} className="space-y-2">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </SkeletonGroup>
        )}
        {recent.data && items.length === 0 && <p className="text-sm text-hint">{t.home.empty}</p>}
        {items.length > 0 && (
          <ul className="space-y-2">
            {items.map((th) => (
              <li key={th.id}>
                <Link
                  to={`/thoughts/${th.id}`}
                  onClick={() => haptic('light')}
                  className="flex items-center gap-3 p-3 rounded-xl bg-surface shadow-card transition-opacity active:opacity-80"
                >
                  <span className="flex-1 min-w-0 text-sm truncate">{firstLine(th.excerpt)}</span>
                  <Badge tone={THOUGHT_TONE[th.status]} className="flex-shrink-0">
                    {t.home.status[th.status]}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {toast}
    </div>
  );
}
